import {Component, OnInit, HostListener, ChangeDetectionStrategy} from "@angular/core";
import {NgForm} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {AuthService} from "src/app/services/auth.service";
import {PerformaModulesService} from "src/app/services/performa-modules.service";
import {
    GroupDto,
    ModuleDto,
    PerFormaAPIService,
    UserDocumentTypeDto,
    UserDto,
    UserPermissionsDto
} from "src/app/services/pfapi.service";

@Component({
    selector: "app-user-edit",
    templateUrl: "./user-edit.component.html",
    styleUrl: "./user-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserEditComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private router: Router,
        private message: NzMessageService,
        private readonly ModulesService: PerformaModulesService,
        private readonly auth: AuthService
    ) {}

    loading = true;
    myUsername = "";
    isEditMode = false;
    force2fa = false;

    // the following object is used to check
    // if any unsaved changes are present when
    // the user attempts to navigate away
    originalUser: UserDto | null = null;

    apCheckEnabled = false;
    apCheckRole: "User" | "Admin" = "User";
    passwordConfirm = "";
    selectedDocType = "";
    selectableDocTypes: {label: string; value: string}[] = [];
    userDocTypes: UserDocumentTypeDto[] = [];

    selectedGroup = "";
    selectableGroups: {label: string; value: string}[] = [];
    userGroups: GroupDto[] = [];

    groupColumns = [
        {
            title: "Group Name"
        },
        {
            title: ""
        }
    ];

    modules: ModuleDto[] = [];

    columns = [
        {
            title: "Document Type"
        },
        {
            title: "Upload File"
        },
        {
            title: "Edit Keyword Data"
        },
        {
            title: "Add Notes"
        },
        {
            title: "Delete Documents"
        },
        {
            title: "View Scans"
        },
        {
            title: "Delete Scans"
        },
        {
            title: ""
        }
    ];

    // default user object. replaced if this is an edit
    user: UserDto = {
        UserID: null,
        UserName: "",
        Email: "",
        UserPassword: "",
        PasswordChanged: false,
        TwoFactorEnabled: false,
        Permissions: {
            PerFormaAdmin: false,
            PerFormaScanAdmin: false,
            APCheckAdmin: false,
            APCheckUser: false,
            ARReportUser: false,
            FINReportUser: false
        } as UserPermissionsDto,
        DocumentTypes: []
    };

    async ngOnInit() {
        this.route.queryParams.subscribe(p => {
            if (p.created === "true") {
                this.message.success("Created user");
            }
            if (p.restored === "true") {
                this.message.success(
                    "This user has been restored. Please review the permissions and make any necessary changes."
                );
            }
        });

        // get a list of Performa modules to determine which columns to show
        this.modules = (await this.ModulesService.Modules()) ?? [];

        // is 2FA required?
        this.force2fa = this.ModulesService.features.some((x: string) => x.toLowerCase() === "force2fa");

        this.myUsername = this.auth.username;

        await this.checkEditMode();
    }

    checkEditMode = async () => {
        const id = this.route.snapshot.queryParamMap.get("id");

        try {
            if (!id) {
                await this.getAllGroups();
                return;
            }

            this.isEditMode = true;

            this.user = await lastValueFrom(this.api.getApiConfigureUsersId(Number(id)));
            this.originalUser = JSON.parse(JSON.stringify(this.user));

            this.initializeAPCheck();
            this.setGroups();
            this.setDocTypes();
        } catch (e: any) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    // we only use this method for creation of a new user
    getAllGroups = async () => {
        const allGroups = await lastValueFrom(this.api.getApiConfigureGroupsAll());

        this.user.Groups =
            allGroups?.Data?.map(x => {
                return {
                    UserGroupID: null,
                    UserID: 0,
                    GroupID: x.GroupID,
                    GroupName: x.GroupName
                };
            }) ?? [];

        this.setGroups();
    };

    initializeAPCheck = () => {
        this.apCheckEnabled = this.user?.Permissions?.APCheckAdmin || this.user?.Permissions?.APCheckUser || false;
        this.apCheckRole = this.user?.Permissions?.APCheckAdmin ? "Admin" : "User";

        if (this.apCheckEnabled && !this.user?.Permissions?.APCheckAdmin && !this.user?.Permissions?.APCheckUser) {
            if (this.user.Permissions) this.user.Permissions.APCheckUser = true;
        }
    };

    onApCheckEnabledChange = (enabled: boolean) => {
        if (!enabled) {
            this.user!.Permissions!.APCheckAdmin = false;
            this.user!.Permissions!.APCheckUser = false;
        } else {
            // default to User when first enabling, then sync the booleans to the selected role
            this.apCheckRole = this.user!.Permissions!.APCheckAdmin ? "Admin" : "User";
            this.applyApCheckRole();
        }
    };

    onApCheckRoleChange = (role: "User" | "Admin") => {
        this.apCheckRole = role;
        this.applyApCheckRole();
    };

    private applyApCheckRole = () => {
        const isAdmin = this.apCheckRole === "Admin";
        this.user!.Permissions!.APCheckAdmin = isAdmin;
        this.user!.Permissions!.APCheckUser = !isAdmin;
    };

    save = async (frm: NgForm) => {
        frm.control.markAllAsTouched();

        if (this.isEditMode) {
            const password = this.user.UserPassword ?? "";

            if (password !== (this.passwordConfirm ?? "")) {
                this.message.error("Passwords do not match");
                return;
            }

            this.user.PasswordChanged = password !== "";
        }

        if (!frm.valid) return;
        this.loading = true;

        try {
            if (this.isEditMode) {
                await lastValueFrom(this.api.putApiConfigureUsers(this.user));

                this.user.PasswordChanged = false;
                this.user.UserPassword = "";
                this.passwordConfirm = "";
                this.message.success("Modified user");
            } else {
                const resp = await lastValueFrom(this.api.postApiConfigureUsers(this.user));
                this.user = resp;
                this.isEditMode = true;
                this.router.navigateByUrl(`/configure/useredit?id=${resp.UserID}&created=true`);
            }

            this.updateOriginalData();

            frm.control.markAsUntouched();
        } catch (e: any) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    remove = async () => {
        if (!confirm("Are you sure you want to delete this user?")) {
            return;
        }
        try {
            this.loading = true;
            await lastValueFrom(this.api.deleteApiConfigureUsersId(Number(this.user.UserID)));
            this.router.navigateByUrl("/configure/users");
        } catch (e: any) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    addDocType = (id: string) => {
        const item = this.user!.DocumentTypes!.find(t => t.DocumentTypeID === Number(id) && !t.DocumentPermissions);

        if (item) {
            item.DocumentPermissions = {
                UploadFile: false,
                EditKeywordData: false,
                AddNotes: false,
                DeleteDocuments: false,
                PerFormaScan: false,
                DeleteScan: false
            };
            item.DocumentTypeSecurityUniqueID = 0;
            this.setDocTypes();
        }
    };

    // if they are removing a document type privilege row, we
    // just null out the permissions and unique id
    // so that it will go back into the dropdown of available types
    removeDocType = (id: number) => {
        const item = this.user!.DocumentTypes!.find(t => t.DocumentTypeID === id && t.DocumentPermissions);

        if (item) {
            item.DocumentPermissions = null;
            item.DocumentTypeSecurityUniqueID = null;
            this.setDocTypes();
        }
    };

    setSelectableDocTypes = () => {
        this.selectableDocTypes =
            this.user?.DocumentTypes?.filter(t => !t.DocumentPermissions).map(t => {
                return {
                    label: t.DocumentTypeName ?? "",
                    value: t.DocumentTypeID?.toString() || ""
                };
            }) ?? [];
    };

    setUserDocTypes = () => {
        this.userDocTypes = this.user?.DocumentTypes?.filter(t => t.DocumentPermissions) || [];
    };

    setDocTypes = () => {
        this.selectedDocType = "";
        this.setSelectableDocTypes();
        this.setUserDocTypes();
    };

    // if they are removing a group row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available groups
    removeGroup = (id: number) => {
        const item = this.user?.Groups?.find(x => x.GroupID === id && x.UserGroupID != null);

        if (item) {
            item.UserGroupID = null;
            this.setGroups();
        }
    };

    assignGroup = (id: string) => {
        const item = this.user?.Groups?.find(x => x.GroupID === Number(id) && x.UserGroupID == null);

        if (item) {
            item.UserGroupID = 0;
            this.setGroups();
        }
    };

    setGroups = () => {
        this.selectedGroup = "";
        this.setSelectableGroups();
        this.setUserGroups();
    };

    setSelectableGroups = () => {
        this.selectableGroups =
            this.user?.Groups?.filter(x => x.UserGroupID == null).map(x => {
                return {
                    label: x.GroupName ?? "",
                    value: x.GroupID?.toString() || ""
                };
            }) ?? [];
    };

    setUserGroups = () => {
        this.userGroups = this.user?.Groups?.filter(u => u.UserGroupID != null) ?? [];
    };

    //#region Helper Methods

    moduleIsEnabled(moduleName: string): boolean {
        return this.modules.some(m => m.Name === moduleName && m.IsEnabled);
    }

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalUser) {
            return false;
        }

        return JSON.stringify(this.user) !== JSON.stringify(this.originalUser);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    reset = () => {
        if (!this.isUnsavedChanges()) {
            this.message.info("There are no unsaved changes.");
            return;
        }

        this.user = JSON.parse(JSON.stringify(this.originalUser));
        this.passwordConfirm = "";
        this.originalUser!.PasswordChanged = false;
        this.originalUser!.UserPassword = "";
        this.initializeAPCheck();
        this.setGroups();
        this.setDocTypes();
    };

    resetUser2FA = async () => {
        if (!this.user.UserID || !this.user.TwoFactorEnabled) {
            return;
        }

        this.loading = true;

        await lastValueFrom(this.api.postApiConfigureUsersIdReset2fa(Number(this.user.UserID)))
            .then(() => {
                this.user.TwoFactorEnabled = false;
                this.originalUser!.TwoFactorEnabled = false;

                this.message.success(`Successfully ${this.force2fa ? "reset" : "disabled"} 2FA`);
            })
            .catch((e: any) => {
                console.log(e);

                const errors = e.error?.ModelState?.status;

                if (errors && Array.isArray(errors)) {
                    this.message.error(errors[0]);
                }
            })
            .finally(() => {
                this.loading = false;
            });
    };

    goBack = () => {
        if (this.isUnsavedChanges()) {
            if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
                return;
            }
        }

        this.router.navigateByUrl("/configure/users");
    };

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        const newUser: UserDto = JSON.parse(JSON.stringify(this.user));

        this.originalUser!.PasswordChanged = false;
        this.originalUser!.UserPassword = "";
        this.originalUser!.Email = newUser.Email;
        this.originalUser!.Permissions!.PerFormaAdmin = newUser.Permissions?.PerFormaAdmin;
        this.originalUser!.Permissions!.PerFormaScanAdmin = newUser.Permissions?.PerFormaScanAdmin;
        this.originalUser!.Permissions!.ARReportUser = newUser.Permissions?.ARReportUser;
        this.originalUser!.Permissions!.FINReportUser = newUser.Permissions?.FINReportUser;
        this.originalUser!.Permissions!.APCheckAdmin = newUser.Permissions?.APCheckAdmin;
        this.originalUser!.Permissions!.APCheckUser = newUser.Permissions?.APCheckUser;
        this.originalUser!.Groups = newUser.Groups;
        this.originalUser!.DocumentTypes = newUser.DocumentTypes ?? [];
    };

    //#endregion
}
